import {useEffect, useState} from 'react'
import {createUseStyles} from 'react-jss'
import {useNavigate, useParams} from "react-router-dom";
import CardSection from '../components/CardSection'
import DetailsCard from '../components/DetailsCard'
import {formatDateTimeString} from '../utils/dateHelper'
import {useAppSelector} from '../redux/hooks';
import FullScreenLoadingIndicator from '../components/FullScreenLoadingIndicator';
import useCampaignActions from "../hooks/useCampaignActions";
import {CampaignState, CampaignTranslation, CampaignType} from "../redux/campaignTypes";
import LocalizedMessageList from "../components/Campaign/LocalizedMessageList";
import Button from "../components/Button";
import CreateOrUpdateCampaignModal from "../components/Campaign/CreateOrUpdateCampaignModal";
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import NavLink from "../components/NavLink";
import {faArrowLeft, faSliders, faGift, faLanguage} from "@fortawesome/free-solid-svg-icons";
import {Colors} from "../constants/colors";
import {titleMarginBottom} from '../constants/layout';
import UpdateCampaignTranslationModal from "../components/Campaign/UpdateCampaignTranslationModal";
import AudienceEditorModal from "../components/Campaign/Audience/AudienceEditorModal";
import AudienceCard from "../components/Campaign/Audience/AudienceCard";
import CampaignResultCard from "../components/Campaign/CampaignResultCard";

const CampaignDetails = () => {
  const styles = useStyles()
  const navigate = useNavigate()
  const params = useParams<{ id: string }>()

  const [updateCampaignModalVisible, setUpdateCampaignModalVisible] = useState(false)
  const [updateTranslationModalVisible, setUpdateTranslationModalVisible] = useState(false)
  const [updateAudienceModalVisible, setUpdateAudienceModalVisible] = useState(false)

  const [updatedTranslation, setUpdatedTranslation] = useState<CampaignTranslation>()

  const {getCampaign, launchCampaign, abortCampaign} = useCampaignActions()

  const loading = useAppSelector(state => state.campaigns.loading)
  const campaign = useAppSelector(state => state.campaigns.campaign)
  const error = useAppSelector(state => state.campaigns.error)
  const [showLaunchError, setShowLaunchError] = useState(false)

  const [actions, setActions] = useState({
    canEdit: false,
    canLaunch: false,
    canAbort: false
  })

  const notSpecified = 'Not specified'

  useEffect(() => {
    if (campaign == null || campaign.id !== params.id) {
      getCampaign(params.id || '')
    }

    setActions({
      canEdit: !!campaign && [CampaignState.New, CampaignState.Failed, CampaignState.Aborted].includes(campaign.state),
      canLaunch: !!campaign && [CampaignState.New, CampaignState.Aborted].includes(campaign.state),
      canAbort: !!campaign && [CampaignState.New, CampaignState.Launched, CampaignState.Failed].includes(campaign.state)
    })
  }, [campaign, params.id])

  useEffect(() => {
    if (!!error) {
      setShowLaunchError(true)
    } else {
      setShowLaunchError(false)
    }
  }, [error]);

  const handleLaunchCampaign = () => {
    if (!!campaign) {
      launchCampaign(campaign.id)
    }
  }

  const handleAbortCampaign = () => {
    if (!!campaign) {
      abortCampaign(campaign.id)
    }
  }

  const handleEditTranslation = (message: CampaignTranslation | undefined) => {
    setUpdatedTranslation(message)
    setUpdateTranslationModalVisible(true)
  }

  const renderCampaignInformation = () => {
    switch (campaign?.state) {
      case CampaignState.Ongoing:
        return <Alert
          key='ongoingAlert'
          variant='info'>
          <p>Campaign is currently executing. A result should be available soon.</p>
        </Alert>
      case CampaignState.Launched:
        return <Alert
          key='launchAlert'
          variant='warning'>
          <p>Campaign is launched, and will execute at {formatDateTimeString(campaign?.executionTime)}.</p>
          <p>If you need to modify the campaign you need to abort it first.</p>
        </Alert>
      case CampaignState.Completed:
        return <Alert
          key='successAlert'
          variant='success'>
          Campaign is completed
        </Alert>
      case CampaignState.Failed:
        return <Alert
          key='failedAlert'
          variant='danger'>
          <p>Campaign failed. It can be re-launched by first aborting it.</p>
          <p>Before re-launching, you need to investigate what went wrong (e.g. check logs).</p>
        </Alert>
      case CampaignState.Aborted:
        return <Alert
          key='abortedAlert'
          variant='secondary'>
          <p>Campaign has been aborted.</p>
          </Alert>
    }
  }

  const renderLaunchError = () => {
    if (showLaunchError) {
      return <Alert
        key='launchError'
        variant='danger'
        onClose={() => setShowLaunchError(false)}
        dismissible>
        {error instanceof Error ? error.message : error}
      </Alert>
    }
  }

  const renderTranslations = () => {
    return <DetailsCard>
      <h6><FontAwesomeIcon icon={faLanguage} className={styles.titleIcon}/> Translations</h6>
      <LocalizedMessageList
        messages={campaign?.messages}
        canEdit={actions.canEdit}
        onEditClick={handleEditTranslation}
      />
    </DetailsCard>
  }

  const getBadgeVariant = () => {
    switch (campaign?.state) {
      case CampaignState.Launched:
      case CampaignState.Ongoing:
        return 'warning'
      case CampaignState.Completed:
        return 'success'
      case CampaignState.Failed:
        return 'danger'
      case CampaignState.Aborted:
        return 'secondary'
      default:
        return 'primary'
    }
  }

  const renderTitleRow = () => {
    return (
      <div className={styles.titleRow}>
        <h3 style={{textTransform: 'capitalize'}}>{`${campaign?.type} campaign`}<h1>{campaign?.name}</h1></h3>
        {campaign &&
            <h1><Badge bg={getBadgeVariant()} className={styles.titleBadge}>{campaign.state}</Badge></h1>}
      </div>
    )
  }

  return (
    <div className={styles.container}>
      {renderTitleRow()}
      <div className={styles.actionBar}>
        <div className={styles.actionBarCell} style={{justifyContent: 'flex-start'}}>
          <NavLink title='Back' icon={faArrowLeft} onClick={() => navigate(-1)}/>
        </div>
        <div className={styles.actionBarCell} style={{justifyContent: 'flex-end'}}>
          <Button title='Launch' disabled={!actions.canLaunch} onClick={() => handleLaunchCampaign()}/>
          <Button title='Abort' disabled={!actions.canAbort} onClick={() => handleAbortCampaign()}/>
        </div>
      </div>
      {renderCampaignInformation()}
      {renderLaunchError()}
      <CardSection>
        <DetailsCard onClick={actions.canEdit ? () => setUpdateCampaignModalVisible(true) : undefined}>
          <h6><FontAwesomeIcon icon={faSliders} className={styles.titleIcon}/> Campaign settings</h6>
          <p>Name: <span>{campaign?.name || notSpecified}</span></p>
          <p>Execution time: <span>{formatDateTimeString(campaign?.executionTime)}</span></p>
          <p>Transport: <span>{campaign?.transportType || notSpecified}</span></p>
          {campaign?.type === CampaignType.FreePayout &&
              <>
                  <p><FontAwesomeIcon icon={faGift} className={styles.titleIcon}/>Number of free
                      payouts: <span>{'numberOfFreePayouts' in campaign ? campaign.numberOfFreePayouts : notSpecified}</span>
                  </p>
              </>}
        </DetailsCard>
      </CardSection>

      <CardSection>
        <AudienceCard
          audience={campaign?.audience}
          canEdit={actions.canEdit}
          onEdit={() => setUpdateAudienceModalVisible(true)}
        />
      </CardSection>

      {!!campaign?.result && (
        <CardSection>
          <CampaignResultCard result={campaign.result}/>
        </CardSection>
      )}

      <CardSection>
        {renderTranslations()}
      </CardSection>

      {!!campaign &&
          <CreateOrUpdateCampaignModal
              initialValue={campaign}
              visible={updateCampaignModalVisible}
              onCloseClick={() => setUpdateCampaignModalVisible(false)}
          />
      }

      {!!campaign && !!updatedTranslation &&
          <UpdateCampaignTranslationModal
              campaignId={campaign.id}
              initialValue={updatedTranslation}
              visible={updateTranslationModalVisible}
              onCloseClick={() => setUpdateTranslationModalVisible(false)}
          />
      }

      {!!campaign &&
          <AudienceEditorModal
              campaignId={campaign.id}
              initialValue={campaign.audience}
              visible={updateAudienceModalVisible}
              onCloseClick={() => setUpdateAudienceModalVisible(false)}
          />
      }

      <FullScreenLoadingIndicator visible={loading}/>
    </div>
  )
}

export default CampaignDetails

const useStyles = createUseStyles({
  container: {},
  titleRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 20,
    marginBottom: titleMarginBottom,
  },
  titleIcon: {
    marginRight: 8,
    color: Colors.darkGray2,
  },
  titleBadge: {
    textTransform: 'capitalize'
  },
  actionBar: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem'
  },
  actionBarCell: {
    display: 'flex',
    flexDirection: 'row',
    columnGap: 15,
    alignItems: 'center',
    width: '33%'
  }
})
